import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./Components/organisms/AuthContext";
import { BlogProvider } from "./Components/organisms/BlogContext";
import { PayrollProvider } from "./Components/molecules/payrollManagment/PayrollContext";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toast-custom.css";

import MainLayout from "./Components/organisms/MainLayout";
import PrivateRoute from "./Components/organisms/PrivateRoute";
import RegisterUser from "./Components/organisms/RegisterUser";
import PopupNotice from "./Components/atoms/PopupNotice";
import ChatBotLoader from "./Components/atoms/ChatBotLoader";

// Lazy load components for better code splitting
const AdminTicket = lazy(() => import("./Components/atoms/AdminTicket"));
const AllUpcomingBirthdays = lazy(
  () => import("./Components/atoms/AllUpcomingBirthdays"),
);
const AllWorkAnniversaries = lazy(
  () => import("./Components/atoms/AllWorkAnniversaries"),
);
const AttendanceManager = lazy(
  () => import("./Components/atoms/AttendanceManager"),
);
const AttendanceTicket = lazy(
  () => import("./Components/atoms/AttendanceTicket"),
);
const BlogDetails = lazy(() => import("./Components/atoms/BlogDetails"));
const BlogList = lazy(() => import("./Components/atoms/BlogList"));
const EmployeeLetter = lazy(() => import("./Components/atoms/EmployeeLetter"));
const ExperienceLetter = lazy(
  () => import("./Components/atoms/ExperienceLetter"),
);
const FeedbackForm = lazy(() => import("./Components/atoms/FeedbackForm"));
const FullNotificationsPage = lazy(
  () => import("./Components/atoms/FullNotificationsPage"),
);
const HRTicket = lazy(() => import("./Components/atoms/HRTicket"));
const LeaveApplication = lazy(
  () => import("./Components/atoms/LeaveApplication"),
);
const LeavesAvailable = lazy(
  () => import("./Components/atoms/LeavesAvailable"),
);
const NetworkTicket = lazy(() => import("./Components/atoms/NetworkTicket"));
const NotFound = lazy(() => import("./Components/atoms/NotFound"));
const Notices = lazy(() => import("./Components/atoms/Notices"));
const CreateNotice = lazy(
  () => import("./Components/atoms/notices/CreateNotice"),
);
const NoticesManagement = lazy(
  () => import("./Components/atoms/NoticesManagement"),
);
const NotificationDetailPage = lazy(
  () => import("./Components/atoms/NotificationDetailPage"),
);
const Policies = lazy(() => import("./Components/atoms/Policies"));
const ProvidentFund = lazy(() => import("./Components/atoms/ProvidentFund"));
const UserProfileUpdater = lazy(
  () => import("./Components/atoms/SpecificUserUpdate/UserProfileUpdater"),
);
const SubmitATicket = lazy(() => import("./Components/atoms/SubmitATicket"));
const SuggestionForm = lazy(() => import("./Components/atoms/SuggestionForm"));
const TicketStatus = lazy(() => import("./Components/atoms/TicketStatus"));
const TrackApplication = lazy(
  () => import("./Components/atoms/TrackApplication"),
);
const ViewAttendance = lazy(() => import("./Components/atoms/ViewAttendance"));
const UserPasswordManager = lazy(
  () => import("./Components/atoms/UserPasswordManager"),
);
const ViewHolidays = lazy(() => import("./Components/atoms/ViewHolidays"));
const ProcessPayroll = lazy(() => import("./Components/atoms/ProcessPayroll"));
const UserAttendanceDetails = lazy(
  () => import("./Components/atoms/UserAttendanceDetails"),
);

// Molecule components
const AddNewEmployee = lazy(
  () => import("./Components/molecules/AddNewEmployee"),
);
const AttendanceManagement = lazy(
  () => import("./Components/molecules/AttendanceManagement"),
);
const MasterDataManagement = lazy(
  () => import("./Components/molecules/MasterDataManagement"),
);
const EditablePayrollPage = lazy(
  () => import("./Components/molecules/EditablePayrollPage"),
);
const EditProfilePage = lazy(
  () => import("./Components/molecules/EditProfilePage"),
);
const EmployeeManagement = lazy(
  () => import("./Components/molecules/EmployeeManagement"),
);
const DeactivatedUsersList = lazy(
  () => import("./Components/organisms/DeactivatedUsersList"),
);
const DeactivatedUserDetails = lazy(
  () => import("./Components/organisms/DeactivatedUserDetails"),
);
const ForgotPasswordPage = lazy(
  () => import("./Components/molecules/ForgotPasswordPage"),
);
const FormsManagement = lazy(
  () => import("./Components/molecules/FormsManagement"),
);
const HolidayManagement = lazy(
  () => import("./Components/molecules/HolidayManagement"),
);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LeaveManagement = lazy(
  () => import("./Components/molecules/LeaveManagement"),
);
const LeavesManagementDashboard = lazy(
  () => import("./Components/molecules/LeavesManagementDashboard"),
);
const SiginPage = lazy(() => import("./Components/molecules/SignPage"));
const TeamManagement = lazy(
  () => import("./Components/molecules/TeamManagement"),
);
const TeamDashboard = lazy(
  () => import("./Components/molecules/TeamDashboard"),
);
const TeamMemberDetail = lazy(
  () => import("./Components/molecules/TeamMemberDetail"),
);
const TicketManagement = lazy(
  () => import("./Components/molecules/TicketManagement"),
);
const UserShiftManagement = lazy(
  () => import("./Components/molecules/UserShiftManagement"),
);
const VehicleManagement = lazy(
  () => import("./Components/molecules/VehicleManagement"),
);
const TrainingRoom = lazy(() => import("./Components/molecules/TrainingRoom"));
const PayrollManagement = lazy(
  () => import("./Components/molecules/payrollManagment/PayrollManagement"),
);
const ViewPayroll = lazy(
  () => import("./Components/molecules/payrollManagment/ViewPayroll"),
);
const SalarySlip = lazy(() => import("./Components/molecules/SalarySlip"));
const NewPayrollWrapper = lazy(
  () => import("./Components/NewPayroll/NewPayrollWrapper"),
);
const PayrollDetails = lazy(
  () => import("./Components/NewPayroll/PayrollDetails"),
);
const FinanceOverview = lazy(
  () => import("./Components/NewPayroll/FinanceOverview"),
);
const EmployeeLoansListPage = lazy(
  () => import("./features/employee-loans/pages/EmployeeLoansListPage"),
);
const EmployeeLoanNewPage = lazy(
  () => import("./features/employee-loans/pages/EmployeeLoanNewPage"),
);
const EmployeeLoanDetailPage = lazy(
  () => import("./features/employee-loans/pages/EmployeeLoanDetailPage"),
);
const MyLoansPage = lazy(
  () => import("./features/employee-loans/pages/MyLoansPage"),
);
const EmployeeLettersListPage = lazy(
  () => import("./features/employee-letters/pages/EmployeeLettersListPage"),
);
const EmployeeLetterNewPage = lazy(
  () => import("./features/employee-letters/pages/EmployeeLetterNewPage"),
);
const EmployeeLetterDetailPage = lazy(
  () => import("./features/employee-letters/pages/EmployeeLetterDetailPage"),
);
const MyLettersInboxPage = lazy(
  () => import("./features/employee-letters/pages/MyLettersInboxPage"),
);
const TrustedDevicesAdminPage = lazy(
  () => import("./features/trusted-devices/pages/TrustedDevicesAdminPage"),
);
const ResetPasswordPage = lazy(
  () => import("./Components/molecules/ResetPasswordPage"),
);
const PollManagement = lazy(
  () => import("./Components/molecules/PollManagement"),
);
const LeaveBalanceApprovalDashboard = lazy(
  () => import("./Components/molecules/LeaveBalanceApprovalDashboard"),
);
const LeaveBalanceMyRequests = lazy(
  () => import("./Components/molecules/LeaveBalanceMyRequests"),
);

const DashboardLayout = lazy(
  () => import("./Components/organisms/DashboardLayout"),
);
const PayrollFinanceAnalytics = lazy(
  () => import("./Components/organisms/PayrollFinanceAnalytics"),
);
const VehicleFinanceAnalytics = lazy(
  () => import("./Components/organisms/VehicleFinanceAnalytics"),
);
const TaxesAnalytics = lazy(
  () => import("./Components/organisms/TaxesAnalytics"),
);
const AttendanceApprovalDashboard = lazy(
  () => import("./Components/molecules/AttendanceApprovalDashboard"),
);
const AttendanceMyRequests = lazy(
  () => import("./Components/molecules/AttendanceMyRequests"),
);
const AttendanceActivityFeed = lazy(
  () => import("./Components/molecules/AttendanceActivityFeed"),
);

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
  </div>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <div className="font-sans">
        <PopupNotice />
        <ChatBotLoader />
        <BrowserRouter>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route
                path="/reset-password/:token"
                element={<ResetPasswordPage />}
              />
              <Route path="/" element={<SiginPage />} />
              <Route path="/login" element={<SiginPage />} />
              <Route path="/register" element={<RegisterUser />} />

              <Route element={<PrivateRoute />}>
                <Route element={<MainLayout />}>
                  <Route path="/dashboard" element={<DashboardLayout />} />
                  <Route path="/forms/feedback" element={<FeedbackForm />} />
                  <Route
                    path="/forms/suggestion"
                    element={<SuggestionForm />}
                  />
                  <Route
                    path="/forms/leave-application"
                    element={<LeaveApplication />}
                  />
                  <Route
                    path="/organization/payroll-management/process"
                    element={<ProcessPayroll />}
                  />
                  <Route
                    path="/forms/track-application"
                    element={<TrackApplication />}
                  />
                  <Route path="/attendance/view" element={<ViewAttendance />} />
                  <Route
                    path="/attendance/view-holidays"
                    element={<ViewHolidays />}
                  />
                  <Route path="/payroll/view" element={<SalarySlip />} />

                  {/* New Payroll System - User View */}
                  <Route
                    path="/payroll/new-view"
                    element={<NewPayrollWrapper isAdminView={false} />}
                  />
                  <Route
                    path="/payroll/new-view/:id"
                    element={<PayrollDetails />}
                  />

                  <Route
                    path="/payroll/my-loans/:id"
                    element={<EmployeeLoanDetailPage />}
                  />
                  <Route path="/payroll/my-loans" element={<MyLoansPage />} />

                  <Route
                    path="/payroll/available-leaves"
                    element={<LeavesAvailable />}
                  />
                  <Route
                    path="/payroll/provident-fund"
                    element={<ProvidentFund />}
                  />
                  <Route
                    path="/tickets/attendance"
                    element={<AttendanceTicket />}
                  />
                  <Route path="/tickets/network" element={<NetworkTicket />} />
                  <Route path="/tickets/hr" element={<HRTicket />} />
                  <Route path="/tickets/admin" element={<AdminTicket />} />
                  <Route path="/tickets/status" element={<TicketStatus />} />
                  <Route path="/policies" element={<Policies />} />
                  <Route path="/announcements" element={<Notices />} />
                  <Route path="/edit-profile" element={<EditProfilePage />} />
                  <Route path="/create-ticket" element={<SubmitATicket />} />
                  <Route
                    path="/blog"
                    element={
                      <BlogProvider>
                        <BlogList />
                      </BlogProvider>
                    }
                  />
                  <Route
                    path="/blog/:blogId"
                    element={
                      <BlogProvider>
                        <BlogDetails />
                      </BlogProvider>
                    }
                  />
                  <Route
                    path="/organization/employee-management"
                    element={<EmployeeManagement />}
                  />
                  <Route
                    path="/organization/employee-management/add-new-employee"
                    element={<AddNewEmployee />}
                  />
                  <Route
                    path="/organization/employee-management/deactivated-employees"
                    element={<DeactivatedUsersList />}
                  />
                  <Route
                    path="/organization/employee-management/deactivated-employees/:userId"
                    element={<DeactivatedUserDetails />}
                  />
                  {/* <Route
                  path="/organization/payroll-management"
                  element={<PayrollManagement />}
                /> */}

                  <Route
                    path="/organization/payroll-management"
                    element={
                      <PayrollProvider>
                        <PayrollManagement />
                      </PayrollProvider>
                    }
                  />
                  {/* <Route
                  path="/edit/:id"
                  element={
                    <PayrollProvider>
                      <EditPayroll />
                    </PayrollProvider>
                  }
                /> */}

                  <Route
                    path="/view/:id"
                    element={
                      <PayrollProvider>
                        <ViewPayroll />
                      </PayrollProvider>
                    }
                  />

                  <Route
                    path="/organization/payroll-management/edit/:id"
                    element={<EditablePayrollPage />}
                  />
                  <Route
                    path="/organization/payroll-management/:id"
                    element={<EditablePayrollPage />}
                  />

                  {/* New Payroll System - Admin View */}
                  <Route
                    path="/organization/new-payroll"
                    element={<NewPayrollWrapper isAdminView={true} />}
                  />
                  <Route
                    path="/organization/new-payroll/:id"
                    element={<PayrollDetails />}
                  />

                  {/* Employee loans (HR / Finance / manager directory; employees use /payroll/my-loans) */}
                  <Route
                    path="/organization/employee-loans/new"
                    element={<EmployeeLoanNewPage />}
                  />
                  <Route
                    path="/organization/employee-loans/:id"
                    element={<EmployeeLoanDetailPage />}
                  />
                  <Route
                    path="/organization/employee-loans"
                    element={<EmployeeLoansListPage />}
                  />

                  {/* Employee letters (HR / manager issue; employees use /letters/inbox) */}
                  <Route
                    path="/organization/employee-letters/new"
                    element={<EmployeeLetterNewPage />}
                  />
                  <Route
                    path="/organization/employee-letters/:id"
                    element={<EmployeeLetterDetailPage />}
                  />
                  <Route
                    path="/organization/employee-letters"
                    element={<EmployeeLettersListPage />}
                  />

                  {/* Finance Overview - Super Admin & Finance */}
                  <Route
                    path="/organization/finance-overview"
                    element={<FinanceOverview />}
                  />

                  <Route
                    path="/organization/ticket-management"
                    element={<TicketManagement />}
                  />
                  <Route
                    path="/organization/holiday-management"
                    element={<HolidayManagement />}
                  />
                  <Route
                    path="/organization/user-shift-management"
                    element={<UserShiftManagement />}
                  />
                  <Route
                    path="/organization/master-data"
                    element={<MasterDataManagement />}
                  />
                  <Route
                    path="/organization/master-data/:section"
                    element={<MasterDataManagement />}
                  />
                  <Route
                    path="/organization/notice-management"
                    element={<NoticesManagement />}
                  />
                  <Route
                    path="/organization/forms-management"
                    element={<FormsManagement />}
                  />
                  <Route
                    path="/organization/vehicle-management"
                    element={<VehicleManagement />}
                  />
                  <Route
                    path="/organization/attendance-management"
                    element={<AttendanceManagement />}
                  />
                  <Route
                    path="/organization/attendance-approval"
                    element={<AttendanceApprovalDashboard />}
                  />
                  <Route
                    path="/attendance/my-requests"
                    element={<AttendanceMyRequests />}
                  />
                  <Route
                    path="/organization/attendance-logs"
                    element={<AttendanceActivityFeed />}
                  />
                  <Route
                    path="/organization/trusted-devices"
                    element={<TrustedDevicesAdminPage />}
                  />
                  <Route
                    path="/organization/poll-management"
                    element={<PollManagement />}
                  />
                  <Route
                    path="/organization/team-management"
                    element={<TeamManagement />}
                  />
                  <Route
                    path="/organization/leave-management"
                    element={<LeavesManagementDashboard />}
                  />
                  <Route
                    path="/organization/leave-balance-requests"
                    element={<LeaveBalanceApprovalDashboard />}
                  />
                  <Route
                    path="/organization/leave-balance/my-requests"
                    element={<LeaveBalanceMyRequests />}
                  />
                  <Route path="/team/overview" element={<TeamDashboard />} />
                  <Route
                    path="/team/leaves"
                    element={<LeavesManagementDashboard />}
                  />
                  <Route
                    path="/team/member/:memberId"
                    element={<TeamMemberDetail />}
                  />
                  <Route
                    path="/all-anniversaries"
                    element={<AllWorkAnniversaries />}
                  />
                  <Route
                    path="all-upcoming-birthdays"
                    element={<AllUpcomingBirthdays />}
                  />
                  <Route
                    path="/letters/inbox/:id"
                    element={<EmployeeLetterDetailPage />}
                  />
                  <Route
                    path="/letters/inbox"
                    element={<MyLettersInboxPage />}
                  />
                  <Route
                    path="/letters/employee-letter"
                    element={<EmployeeLetter />}
                  />
                  <Route
                    path="/letters/experience-letter"
                    element={<ExperienceLetter />}
                  />

                  <Route
                    path="/edit-profile/:id"
                    element={<UserProfileUpdater />}
                  />
                  <Route
                    path="/notifications"
                    element={<FullNotificationsPage />}
                  />
                  <Route
                    path="/notification-detail"
                    element={<NotificationDetailPage />}
                  />
                  <Route path="/notices/create" element={<CreateNotice />} />

                  <Route
                    path="/organization/attendance-manager"
                    element={<AttendanceManager />}
                  />
                  <Route
                    path="/attendance/user/:userId"
                    element={<UserAttendanceDetails />}
                  />

                  <Route
                    path="/admin/password-manager"
                    element={<UserPasswordManager />}
                  />
                  <Route
                    path="/organization/payroll-finance"
                    element={<PayrollFinanceAnalytics />}
                  />
                  <Route
                    path="/organization/taxes"
                    element={<TaxesAnalytics />}
                  />
                  <Route
                    path="/organization/vehicle-finance"
                    element={<VehicleFinanceAnalytics />}
                  />

                  <Route path="/training-room" element={<TrainingRoom />} />

                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>
            </Routes>
          </Suspense>
          <ToastContainer
            position="top-center"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
            transition={Slide}
            limit={3}
            stacked
          />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
};

export default App;
